import { Heading } from "../../components";
import { motion } from "framer-motion";

const APPLE_BUTTON_SVG = "/images/download-on-the-app-store-apple-logo-svgrepo-com.svg";
const APPLE_APP_STORE_URL = "https://apps.apple.com/ca/app/pagent/id6670614673";

// Animation variants
const textVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.7,
      ease: [0.25, 0.1, 0.25, 1.0] // Cubic bezier for a more natural feel
    }
  }
};

const buttonVariants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.05,
    transition: { duration: 0.2 }
  },
  tap: { 
    scale: 0.98,
    transition: { duration: 0.1 }
  }
};

export default function FeaturesSection() {
  return (
    <div className="features-section relative">
      <div
        className="w-full bg-no-repeat bg-cover bg-center overflow-x-hidden pb-10"
        style={{
          backgroundImage: "url(/images/bg-img.png)",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          position: "relative",
        }}
      >
        {/* Subtle gradient overlay for better text readability */}
        <div 
          className="absolute inset-0" 
          style={{
            background: "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0) 50%)",
            pointerEvents: "none",
          }}
        />
        
        <div className="lg:w-10/12 md:w-11/12 container mx-auto pt-[100px] sm:pt-[130px] md:pt-[130px] lg:pt-[130px] flex flex-col justify-center md:flex-col lg:flex-row min-h-full pb-[50px] md:pb-[50px] lg:pb-[30px] px-[15px] sm:px-0 md:px-0 lg:px-0">
          {/* Hero Image (Mobile First) */}
          <motion.div 
            className="flex justify-center items-center md:hidden"
            initial="hidden"
            animate="visible"
            variants={imageVariants}
          >
            <div className="relative">
              {/* Blue blur effect background for mobile */}
              <div className="absolute w-[120%] h-[120%] top-[-10%] left-[-10%] bg-[#142c5c] rounded-full opacity-70 blur-xl"></div>
              
              {/* Mobile hero image */}
              <motion.img
                src="/images/Herophoto.png"
                className="relative z-10 w-[90%] mx-auto h-auto rounded-[24px] border-4 border-gray-800 shadow-2xl"
                alt="Hero Section"
                style={{ filter: "drop-shadow(0 10px 15px rgba(0,0,0,0.2))" }}
                whileHover={{ 
                  scale: 1.02,
                  transition: { duration: 0.3 }
                }}
              />
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div 
            className="flex flex-col justify-center md:items-start items-center text-center md:text-left lg:w-1/2 md:w-full"
            initial="hidden"
            animate="visible"
            variants={textVariants}
          >
            <Heading
              as="h2"
              className="leading-[34px] sm:leading-[34px] md:leading-[40px] lg:leading-[62px] text-[24px] sm:text-[24px] md:text-[30px] lg:text-[44px] text-custom-title"
            >
              <span
                style={{
                  fontWeight: 700,
                  backgroundClip: "text",
                }}
                className="bg-gradient text-gradient-color text-custom-subtitle text-shadow-sm"
              >
                Video & Live Competitions
              </span>
            </Heading>

            {/* Buttons Container with improved animations */}
            <div className="mt-4 flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
              {/* Apple Store Button */}
              <motion.a
                href={APPLE_APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Download on the App Store"
                variants={buttonVariants}
                initial="initial"
                whileHover="hover"
                whileTap="tap"
                className="transform transition-all duration-200 hover:shadow-lg rounded-lg"
              >
                <img
                  src={APPLE_BUTTON_SVG}
                  alt="Download on the App Store"
                  style={{ maxWidth: "200px" }}
                  className="rounded-md"
                />
              </motion.a>

              {/* Google Play Button */}
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.pagent"
                target="_blank"
                rel="noreferrer"
                aria-label="Get it on Google Play"
                variants={buttonVariants}
                initial="initial"
                whileHover="hover"
                whileTap="tap"
                className="transform transition-all duration-200 hover:shadow-lg rounded-lg"
              >
                <img
                  src="/images/google-play-badge-logo-svgrepo-com.svg"
                  alt="Get it on Google Play"
                  style={{ maxWidth: "200px" }}
                  className="rounded-md"
                />
              </motion.a>
            </div>

            {/* Scroll to see features - with animation */}
            <motion.div 
              className="mt-[30px] sm:mt-[60px] md:mt-[70px] lg:mt-[120px] text-white-0 text-sm flex items-center md:items-start gap-x-2 relative"
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: 1, 
                y: 0,
                transition: {
                  delay: 1,
                  duration: 0.8
                }
              }}
            >
              <motion.div
                animate={{ 
                  y: [0, -5, 0],
                  transition: {
                    repeat: Infinity,
                    duration: 1.5
                  }
                }}
              >
                <img
                  src="/images/Scroll.png"
                  alt="Scroll"
                  className="w-full h-full"
                />
              </motion.div>
              <motion.button 
                className="text-center md:text-left mt-2 md:mt-0 text-custom-scroll font-medium"
                whileHover={{ 
                  textShadow: "0 0 8px rgba(255,255,255,0.5)",
                  transition: { duration: 0.3 }
                }}
                onClick={() => {
                  // Smooth scroll to the next section
                  const nextSection = document.querySelector('.features-section').nextElementSibling;
                  if (nextSection) {
                    nextSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Scroll to see features
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Desktop Image with enhanced animation */}
          <motion.div 
            className="hidden md:absolute lg:relative bottom-0 right-0 md:right-[30px] sm:right-0 lg:right-[30px] md:flex lg:w-1/2 md:w-[60%] lg:pl-0 md:pl-0 lg:justify-end md:justify-end"
            initial="hidden"
            animate="visible"
            variants={imageVariants}
          >
            <div className="relative bg-no-repeat h-full">
              <div className="w-full h-full flex justify-center lg:justify-center md:justify-end items-center">
                {/* Blue blur effect background */}
                <div className="absolute w-[120%] h-[120%] bg-[#142c5c] rounded-full opacity-70 blur-xl"></div>
                
                {/* Hero image on top of blur */}
                <motion.img
                  src="/images/Herophoto.png"
                  className="relative z-10 w-[90%] md:w-[55%] lg:w-[90%] h-auto max-w-[600px] max-h-[650px] object-contain rounded-[28px] border-4 border-gray-800 shadow-2xl"
                  alt="Hero Section"
                  style={{ filter: "drop-shadow(0 20px 30px rgba(0,0,0,0.15))" }}
                  whileHover={{ 
                    scale: 1.03,
                    transition: { duration: 0.5 }
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}